import CircularProgress from "@mui/material/CircularProgress";
import Image from "next/image";
import { useRef, useState } from "react";

import iconPdf from "public/icons/icon_pdf.png";
import iconError from "public/img/picto-error-red.svg";

import { useDocumentUpload } from "../../hooks/use-document-upload";

interface DocumentUploaderProps {
  disabled: boolean;
  documentName: string;
  existingDocument?: {
    id: string;
    url: string;
    name: string;
  };
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const DocumentUploader = ({
  disabled,
  documentName,
  existingDocument,
  selectedFile,
  setSelectedFile,
  onError,
}: DocumentUploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errorCheck, setErrorCheck] = useState<string>("");
  const maxSize = 4000000; // 4Mo pour les PDF

  const isFileValid = (file: File) => {
    setErrorCheck("");
    if (file.size > maxSize) {
      setErrorCheck(
        "La taille du fichier (" +
          (file.size / 1000 / 1000).toFixed(2) +
          "Mo) dépasse 4Mo",
      );
      return false;
    } else if (file.type !== "application/pdf") {
      setErrorCheck("Le format du fichier doit être .pdf");
      return false;
    }
    return true;
  };

  const { isPending, error: uploadError } = useDocumentUpload({ isFileValid });

  const handleClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      if (isFileValid(files[0])) {
        if (!documentName || documentName.trim() === "") {
          setErrorCheck("Veuillez saisir un nom pour le document");
          onError?.("Veuillez saisir un nom pour le document");
          return;
        }

        setSelectedFile(files[0]);
      } else {
        onError?.(errorCheck);
      }
    }
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div
        style={{
          backgroundColor: "white",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        onClick={handleClick}
        onKeyDown={(e) => {
          // Empêche la touche Entrée de soumettre un formulaire parent
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div
          className={`flex flex-col items-center justify-center h-full relative ${
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
          style={{
            minWidth: "100%",
            minHeight: "100px",
            border: "2px dashed #9B9B9B",
            borderRadius: "10px",
            padding: "10px",
          }}
        >
          {errorCheck || uploadError ? (
            <div
              className="flex flex-col gap-2 justify-center items-center h-full"
              style={{
                color: "#EE4540",
                fontSize: "12px",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              <Image src={iconError} alt="error" width={30} height={30} />
              <p>{errorCheck || uploadError?.message}</p>
            </div>
          ) : isPending ? (
            <div className="flex justify-center items-center h-full">
              <CircularProgress sx={{ color: "#9B9B9B" }} />
            </div>
          ) : selectedFile || existingDocument ? (
            <div className="flex items-center gap-3">
              <Image src={iconPdf} alt="pdf" width={30} height={30} />
              <p
                style={{
                  fontSize: "14px",
                  color: "#000",
                }}
              >
                {selectedFile?.name || existingDocument?.name || "Document.pdf"}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Image
                src={iconPdf}
                alt="ajouter document"
                width={40}
                height={40}
              />
              <p
                style={{
                  fontSize: "14px",
                  color: "#9B9B9B",
                  marginTop: "5px",
                }}
              >
                Cliquez pour ajouter un PDF
              </p>
              <p
                style={{
                  fontSize: "10px",
                  color: "#9B9B9B",
                }}
              >
                (Max 4Mo)
              </p>
            </div>
          )}

          {/* Input file caché */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".pdf"
            style={{ display: "none" }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              // Empêche la touche Entrée de soumettre un formulaire parent
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};
